import { Divider, Drawer, Slider, Space, Checkbox, Tooltip, Select } from "antd";
import React, { useState } from "react";
import EditorPanelHeading from "../../UI/EditorPanelHeading";
import CalendarUI from "../../UI/CalendarUI";
import { PicRightOutlined, PicLeftOutlined, PicCenterOutlined } from '@ant-design/icons';
import { getColor } from "../../../utils/ConvertCSS";
import { useTemplate } from "../../../utils/Template";
import BMInput from "../../comps/BMInput";
import ColourPicker from "../../../globals/styles/ColourPicker";
import FontPicker from "../../../globals/styles/FontPicker";
import LinkPicker from "../../../globals/styles/LinkPicker";
import { PaddingHorizontalIcon, PaddingVerticalIcon } from "../../Icons";
import { tt_evt_padding_x, tt_evt_padding_y, tt_name_block_warning } from "../../../utils/Utils";
import PresetPicker from "../../../globals/styles/PresetPicker";
import CategorySelector from "../../UI/CategorySelector";

export default function EventsCalendar(props) {

    const { edit = false, close = () => { }, saveItem = () => { }, deleteItem = () => { }, id = 0 } = props;
    const [template,] = useTemplate();
    const [content, setContent] = useState(props.content);

    const setData = (_data) => {
        setContent({ ...content, "data": _data })
    }

    // Component agnostic
    const {
        name = "",
        container = true,
        data = {},
        date_changed = 0
    } = content;

    // Component specific
    const {
        width = 500,
        full_width = false,
        align = "center",
        calendar_colour = 0,
        background_colour = 0,
        highlight_colour = 0,
        active_colour = 0,
        text_colour = 0,
        padding_x = 0,
        padding_y = 0,
        link_style = 0,
        font = 0,
        display_titles = false,

        // New
        date_bar_preset = 0,
        day_preset = 0,
        border_colour = 0,
        day_border_colour = 0,
        calendar_header_colour = 0,
        weekdays_background_colour = 0,
        prev_next_background_colour = 0,

        categories = [],
        display_categories = false


    } = data;


    const save = () => {
        if (!name) {
            let _name = prompt(tt_name_block_warning);
            if (_name) {
                close();
                saveItem(id, { ...content, "name": _name });
            } else {
                return;
            }
        } else {
            close();
            saveItem(id, { ...content })
        }
    }

    const trash = () => {
        deleteItem(id);
        close();
    }

    const marks = {
        300: <small>300px</small>,
        500: <small>500px</small>,
        900: <small>900px</small>,
        1200: <small>1200px</small>,
    };


    const drawRender = () => {
        let _align;
        switch (align) {
            case "center":
                _align = "0 auto"
                break;
            case "right":
                _align = "0 0 0 auto"
                break
            default:
                _align = "0 0 0 0"
        }
        let _bgStyle = {
            background: getColor(background_colour, template),
            padding: padding_y + "px " + padding_x + "px"
        }
        let _wrapperStyle = {
            width: (full_width) ? "100%" : width + "px",
            maxWidth: "100%",
            margin: _align,
            border: "1px solid " + getColor(border_colour, template, "#")
        }

        if (border_colour) {
            _wrapperStyle.border = "1px solid " + getColor(border_colour, template, "#000");
        }


        return (
            <div id={"bm-" + id} className={(container) ? "preview-container" : ""}>
                <div className={"bm-font-" + font} style={_bgStyle}>
                    <div style={_wrapperStyle}>
                        <CalendarUI
                            display_titles={display_titles}
                            display_categories={display_categories}
                            active_colour={active_colour}
                            highlight_colour={highlight_colour}
                            text_colour={text_colour}
                            calendar_colour={calendar_colour}
                            categories={categories}
                            date_bar_preset={date_bar_preset}
                            day_preset={day_preset}
                            calendar_header_colour={calendar_header_colour}
                            weekdays_background_colour={weekdays_background_colour}
                            prev_next_background_colour={prev_next_background_colour}
                            day_border_colour={day_border_colour}
                        />
                    </div>
                </div>
            </div>
        )
    }

    const drawForm = () => {
        return (
            <>
                <EditorPanelHeading menus={props.menus} close={close} trash={trash} save={save} content={content} setContent={setContent} date_changed={date_changed} name={name} />
                {/* ALIGN */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">FONT</Divider>
                <FontPicker value={font} onChange={(e) => setData({ ...data, "font": e })} />
                <div className="shim" /> <div className="shim" />
                {/* ALIGN */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">ALIGN / FULL WIDTH</Divider>
                <div className="float-flex">
                    <Space>
                        <div><PicLeftOutlined onClick={() => setData({ ...data, "align": "left" })} style={{ "color": (align === "left") ? "#1890ff" : "#AAA" }} /></div>
                        <div><PicCenterOutlined onClick={() => setData({ ...data, "align": "center" })} style={{ "color": (align === "center") ? "#1890ff" : "#AAA" }} /></div>
                        <div><PicRightOutlined onClick={() => setData({ ...data, "align": "right" })} style={{ "color": (align === "right") ? "#1890ff" : "#AAA" }} /></div>
                    </Space>
                    <div>
                        <Checkbox checked={full_width} onChange={(e) => setData({ ...data, "full_width": e.target.checked })} ><small>Full width?</small></Checkbox>
                    </div>
                </div>
                <div className="shim" /> <div className="shim" />

                {/* BACKGROUND COLOUR */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">BACKGROUND COLOUR</Divider>
                <ColourPicker allow_gradient allow_none value={background_colour} onChange={(_v) => setData({ ...data, "background_colour": _v })} />
                <div className="shim" /> <div className="shim" />

                {/* WIDTH */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">WIDTH</Divider>
                <Slider disabled={full_width} defaultValue={width} onChange={(e) => setData({ ...data, "width": e })} marks={marks} min={300} max={1248} />
                <div className="shim" /> <div className="shim" />

                {(window.sitesettings.using_events_v2 &&
                    <>
                        {/* CATEGORIES */}
                        <Divider orientationMargin={0} orientation="left" plain className="simple-divider">EVENT CATEGORIES</Divider>
                        <CategorySelector value={categories} onChange={(e) => setData({ ...data, "categories": e })} />
                        <div className="shim" /> <div className="shim" />
                    </>
                )}


                {/* SHOW TITLES */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">DAY CELL DISPLAY</Divider>
                <Select value={(display_titles) ? "titles" : (display_categories) ? "categories" : "dots"} onChange={(e) => {
                    switch (e) {
                        case "titles":
                            setData({ ...data, "display_titles": true, "display_categories": false })
                            break;
                        case "categories":
                            setData({ ...data, "display_titles": false, "display_categories": true })
                            break;
                        default: // dots
                            setData({ ...data, "display_titles": false, "display_categories": false })
                            break;
                    }
                }} style={{ "width": "100%" }}>
                    <Select.Option value="dots"><small>Show Dots</small></Select.Option>
                    <Select.Option value="titles"><small>Show Titles <em>(space permitting)</em></small></Select.Option>
                    {(window.sitesettings.using_events_v2 && <Select.Option value="categories"><small>Show Categories <em>(space permitting)</em></small></Select.Option>)}
                </Select>
                <div className="shim" /> <div className="shim" />

                {/* <Checkbox checked={display_titles} onChange={(e) => setData({ ...data, "display_titles": e.target.checked })} ><small>Show  <em style={{ "opacity": "0.6" }}>(large calendars only)</em></small></Checkbox>
                <div className="shim" /> <div className="shim" /> */}

                {/* DATE PRESET */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">DATE PRESET</Divider>
                <PresetPicker value={date_bar_preset} onChange={(e) => setData({ ...data, "date_bar_preset": e })} />
                <div className="shim" /> <div className="shim" />

                {/* DAY PRESET */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">DAY PRESET</Divider>
                <PresetPicker value={day_preset} onChange={(e) => setData({ ...data, "day_preset": e })} />
                <div className="shim" /> <div className="shim" />

                {/* CALENDAR BACKGROUND COLOUR */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">CALENDAR BACKGROUND COLOUR</Divider>
                <ColourPicker allow_none value={calendar_colour} onChange={(_v) => setData({ ...data, "calendar_colour": _v })} />
                <div className="shim" /> <div className="shim" />

                {/* CALENDAR BORDER COLOUR */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">CALENDAR BORDER COLOUR</Divider>
                <ColourPicker allow_none value={border_colour} onChange={(_v) => setData({ ...data, "border_colour": _v })} />
                <div className="shim" /> <div className="shim" />

                {/* DAY BORDER COLOUR */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">DAY BORDER COLOUR</Divider>
                <ColourPicker allow_none value={day_border_colour} onChange={(_v) => setData({ ...data, "day_border_colour": _v })} />
                <div className="shim" /> <div className="shim" />

                {/* DATE BACKGROUND COLOUR */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">DATE BACKGROUND COLOUR</Divider>
                <ColourPicker allow_none value={calendar_header_colour} onChange={(_v) => setData({ ...data, "calendar_header_colour": _v })} />
                <div className="shim" /> <div className="shim" />

                {/* WEEKDAY BACKGROUND COLOUR */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">WEEKDAY BACKGROUND COLOUR</Divider>
                <ColourPicker allow_none value={weekdays_background_colour} onChange={(_v) => setData({ ...data, "weekdays_background_colour": _v })} />
                <div className="shim" /> <div className="shim" />

                {/* PREV/NEXT MONTH CELL COLOUR */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">PREV/NEXT MONTH COLOUR</Divider>
                <ColourPicker allow_none value={prev_next_background_colour} onChange={(_v) => setData({ ...data, "prev_next_background_colour": _v })} />
                <div className="shim" /> <div className="shim" />

                {/* HIGHLIGHT COLOUR */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">DAY COLOUR</Divider>
                <ColourPicker allow_none value={highlight_colour} onChange={(_v) => setData({ ...data, "highlight_colour": _v })} />
                <div className="shim" /> <div className="shim" />
                {/* ACTIVE CELL COLOUR */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">EVENT DAY COLOUR</Divider>
                <ColourPicker allow_none value={active_colour} onChange={(_v) => setData({ ...data, "active_colour": _v })} />
                <div className="shim" /> <div className="shim" />

                {/* LINK STYLE */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">POP-UP LINK STYLE</Divider>
                <LinkPicker value={link_style} onChange={(e) => setData({ ...data, "link_style": e })} />
                <div className="shim" /> <div className="shim" />

                {/* TEXT COLOUR */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">TEXT COLOUR</Divider>
                <ColourPicker allow_none value={text_colour} onChange={(_v) => setData({ ...data, "text_colour": _v })} />
                <div className="shim" /> <div className="shim" />
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">PADDING</Divider>
                <Space>
                    <BMInput value={padding_x} onChange={(_v) => setData({ ...data, "padding_x": _v })} number min={0} max={100} prefix={<Tooltip title={tt_evt_padding_x}><PaddingHorizontalIcon style={{ "color": "#888" }} /></Tooltip>} />
                    <BMInput value={padding_y} onChange={(_v) => setData({ ...data, "padding_y": _v })} number min={0} max={100} prefix={<Tooltip title={tt_evt_padding_y}><PaddingVerticalIcon style={{ "color": "#888" }} /></Tooltip>} />
                </Space>
                <div className="shim" /> <div className="shim" />
            </>
        )
    }

    return (
        <>
            <Drawer bodyStyle={{ "backgroundColor": "#f5f5f5", "overflowX": "hidden" }} closable={false} width={300} onClose={() => close(true)} open={edit}>
                {drawForm()}
            </Drawer>
            {drawRender()}
        </>
    )

}